import { useState, useEffect, type ReactNode } from "react";
import { usePWAInstall, type BrowserName } from "@/hooks/usePWAInstall";
import { LogoMark } from "@/components/Logo";

function ShareIcon() {
  return (
    <svg className="inline-block h-4 w-4 -mt-0.5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15M9 12l3-3m0 0 3 3m-3-3v12M3 9h18" />
    </svg>
  );
}

function getInstallMessage(isIOS: boolean, browser: BrowserName): ReactNode {
  if (isIOS) {
    switch (browser) {
      case "safari":
        return (
          <>
            In <span className="font-medium">Safari</span>, tap the <ShareIcon /> <span className="font-medium">Share</span> button (bottom or top), then choose <span className="font-medium">&quot;Add to Home Screen&quot;</span> to install and get the full app experience.
          </>
        );
      case "chrome":
        return (
          <>
            In <span className="font-medium">Chrome</span>, tap the <ShareIcon /> <span className="font-medium">Share</span> button (bottom centre), then choose <span className="font-medium">&quot;Add to Home Screen&quot;</span> to install and get the full app experience.
          </>
        );
      case "firefox":
        return (
          <>
            In <span className="font-medium">Firefox</span>, tap the menu (≡) in the bottom-right, then <span className="font-medium">&quot;Add to Home Screen&quot;</span> to install and get the full app experience.
          </>
        );
      case "edge":
        return (
          <>
            In <span className="font-medium">Edge</span>, tap the <ShareIcon /> <span className="font-medium">Share</span> button (bottom centre), then <span className="font-medium">&quot;Add to Home Screen&quot;</span> to install and get the full app experience.
          </>
        );
      default:
        return (
          <>
            Tap the <ShareIcon /> <span className="font-medium">Share</span> button (in the menu bar), then choose <span className="font-medium">&quot;Add to Home Screen&quot;</span> to install and get the full app experience.
          </>
        );
    }
  }
  // Android or desktop
  switch (browser) {
    case "chrome":
      return <>In <span className="font-medium">Chrome</span>, add to your home screen for the best experience — or use the <span className="font-medium">Install</span> button below. Fast access, offline support, and more.</>;
    case "samsung":
      return <>In <span className="font-medium">Samsung Internet</span>, tap the menu (≡) then <span className="font-medium">&quot;Add page to&quot;</span> → <span className="font-medium">Home screen</span> for the full app experience.</>;
    case "firefox":
      return <>In <span className="font-medium">Firefox</span>, tap the menu (≡) then <span className="font-medium">&quot;Install&quot;</span> or add to home screen for the best experience.</>;
    case "edge":
      return <>In <span className="font-medium">Edge</span>, use the menu (⋯) → <span className="font-medium">Apps</span> → <span className="font-medium">Install this site as an app</span> for the full experience.</>;
    default:
      return <>Add to your home screen for the best experience — fast access, offline support, and push notifications.</>;
  }
}

export function InstallBanner() {
  const { canInstall, canPrompt, install, isStandalone, isIOS, browser } = usePWAInstall();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (isStandalone) return;
    const wasDismissed = localStorage.getItem("pwa-install-dismissed");
    if (wasDismissed) {
      const dismissedAt = parseInt(wasDismissed, 10);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }
    setDismissed(false);
  }, [isStandalone]);

  if (!canInstall || dismissed) return null;

  function handleDismiss() {
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
    setDismissed(true);
  }

  async function handleInstall() {
    if (canPrompt) {
      const accepted = await install();
      if (!accepted) handleDismiss();
    }
  }

  return (
    <div className="fixed bottom-20 left-3 right-3 z-[80] animate-slide-up lg:bottom-6 lg:left-auto lg:right-6 lg:w-96">
      <div className="rounded-2xl bg-white dark:bg-slate-800 p-4 shadow-xl shadow-slate-900/10 dark:shadow-black/20 ring-1 ring-slate-900/5 dark:ring-slate-600">
        <div className="flex items-start gap-3">
          <LogoMark size={44} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Install RentMaadi
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
              {getInstallMessage(isIOS, browser)}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="shrink-0 rounded-lg p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            aria-label="Dismiss"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleDismiss}
            className="flex-1 rounded-xl border border-slate-200 dark:border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-[0.98] transition-all"
          >
            Not now
          </button>
          {isIOS ? (
            <button
              onClick={handleDismiss}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 active:scale-[0.98] transition-all"
            >
              Got it
            </button>
          ) : (
            <button
              onClick={handleInstall}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 active:scale-[0.98] transition-all"
            >
              Install
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
